import { css, cx } from "@emotion/css";
import { IconButton, tableStyles, Tooltip } from "@octopusdeploy/design-system-components";
import { ExclamationTriangleIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDApplicationResource } from "@octopusdeploy/octopus-server-client";
import React, { Fragment, useState } from "react";
import type { ArgoCDInstanceApplicationsPageLoaderData } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceApplicationsPage";
import { ArgoCDApplicationDetails, isMissingRequiredAnnotations, hasSourcesWithoutMatchedGitCredentials } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationDetails";
import { ArgoCDApplicationEnvironmentsChipList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationEnvironmentsChipList";
import { ArgoCDApplicationProjectsChipList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationProjectsChipList";
import { ArgoCDApplicationTenantsChipList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationTenantsChipList";
import { EmptyStateNoAnnotations } from "~/areas/infrastructure/components/ArgoCDInstances/components/EmptyStateNoAnnotations";
const styles = {
    flexRow: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    nameTh: css({
        paddingLeft: `${space[72]} !important`,
    }),
    nameTd: css({
        gap: space[32],
    }),
    detailsTd: css({
        padding: "0 !important",
    }),
    tenantsTd: css({
        justifyContent: "space-between",
    }),
};
type ArgoCDApplicationsTableProps = ArgoCDInstanceApplicationsPageLoaderData & {
    showAnnotationsDrawer: () => void;
    onGitCredentialAdded: () => void;
    showHelmAnnotationsDrawer: (selectedApplicationId: string) => void;
};
function hasAnyOctopusAnnotations(application: ArgoCDApplicationResource) {
    return Object.keys(application.Annotations).some((key) => key.startsWith("argo.octopus.com/"));
}
export const ArgoCDApplicationsTable = (props: ArgoCDApplicationsTableProps) => {
    const [expandedRowsMap, setExpandedRowsMap] = useState<Record<string, boolean>>({});
    return (<table className={tableStyles} aria-label="argo cd applications table">
            <thead>
                <tr>
                    <th className={styles.nameTh}>Argo CD Application</th>
                    <th>Projects</th>
                    <th>Environments</th>
                    <th>Tenants</th>
                </tr>
            </thead>
            <tbody>
                {props.applications.map((app) => {
            const isExpanded = expandedRowsMap[app.Name];
            const sourcesWithoutMatchedGitCredentials = hasSourcesWithoutMatchedGitCredentials(app);
            const missingRequiredAnnotations = isMissingRequiredAnnotations(app);
            const showWarning = sourcesWithoutMatchedGitCredentials || missingRequiredAnnotations;
            const warningMessage = sourcesWithoutMatchedGitCredentials ? "Requires Git credential" : missingRequiredAnnotations ? "Missing required annotations" : undefined;
            return (<Fragment key={app.Name}>
                            <tr>
                                <td aria-label={"Argo CD Application"}>
                                    <div className={cx(styles.flexRow, styles.nameTd)}>
                                        <span>
                                            <IconButton onClick={() => setExpandedRowsMap((prev) => ({ ...prev, [app.Name]: !isExpanded }))} icon={isExpanded ? "ChevronDownIcon" : "ChevronRightIcon"}/>
                                        </span>
                                        <span>{app.Name}</span>
                                    </div>
                                </td>
                                <td aria-label={"Projects"}>
                                    <ArgoCDApplicationProjectsChipList projects={props.projects} application={app}/>
                                </td>
                                <td aria-label={"Environments"}>
                                    <ArgoCDApplicationEnvironmentsChipList environments={props.environments} application={app}/>
                                </td>
                                <td aria-label={"Tenants"}>
                                    <div className={cx(styles.flexRow, styles.tenantsTd)}>
                                        <ArgoCDApplicationTenantsChipList tenants={props.tenants} application={app}/>
                                        {showWarning && (<Tooltip content={warningMessage}>
                                                <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.warning}/>
                                            </Tooltip>)}
                                    </div>
                                </td>
                            </tr>
                            {isExpanded && (<tr>
                                    <td className={styles.detailsTd} colSpan={4}>
                                        {hasAnyOctopusAnnotations(app) ? (<ArgoCDApplicationDetails application={app} onGitCredentialAdded={props.onGitCredentialAdded} onGenerateAnnotationsClick={props.showAnnotationsDrawer} onGenerateHelmAnnotationsClick={() => props.showHelmAnnotationsDrawer(app.Id)}/>) : (<EmptyStateNoAnnotations onGenerateAnnotationsClick={props.showAnnotationsDrawer}/>)}
                                    </td>
                                </tr>)}
                        </Fragment>);
        })}
            </tbody>
        </table>);
};
